import { ApolloLink, fromPromise } from '@apollo/client';
import { onError } from '@apollo/client/link/error';

import {
  getErrorCodes,
  isPinCodeError,
  isUnauthorizedError,
} from './graphql-errors';

type AuthErrorLinkHandlers = {
  /** Called on a 428 (pincode/idle lock) — e.g. navigate to the pincode screen. */
  onPinCodeLock: () => void;
  /** Force a fresh token. Resolve with the new token, or null/throw if it can't. */
  refreshToken: () => Promise<string | null>;
  /** Called when the session can't be recovered — e.g. sign out / re-login. */
  onSignOut: () => void;
};

/**
 * A generic Apollo error link that recovers from auth-related failures for
 * EVERY operation, not just one screen:
 *   - 428 (pincode/idle lock) -> onPinCodeLock()
 *   - 401 (expired/invalid token) -> refresh the token once and retry; if it
 *     still fails, onSignOut()
 *
 * It contains no app- or screen-specific logic — the caller injects the
 * pincode-lock / token-refresh / sign-out actions.
 */
export function createAuthErrorLink({
  onPinCodeLock,
  refreshToken,
  onSignOut,
}: AuthErrorLinkHandlers): ApolloLink {
  return onError(({ graphQLErrors, networkError, operation, forward }) => {
    const codes = getErrorCodes(graphQLErrors, networkError);

    if (isPinCodeError(codes)) {
      onPinCodeLock();
      return undefined;
    }

    if (!isUnauthorizedError(codes)) {
      return undefined;
    }

    // Already retried once with a refreshed token and still unauthorized.
    if (operation.getContext().authRetried) {
      onSignOut();
      return undefined;
    }

    operation.setContext({ authRetried: true });
    return fromPromise(
      refreshToken().catch(() => {
        onSignOut();
        return null;
      })
    )
      .filter((token): token is string => Boolean(token))
      .flatMap(() => forward(operation));
  });
}
